import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { Plus, Search, Trash2, Eye, Save, FileText, User, DollarSign, Palette, Edit2, Edit3, X, Check, Calculator, ChevronDown, Package, AlertCircle, Star, Archive, ArchiveRestore, Calendar, PenLine, Send, Mail, MessageSquare, Copy, CheckCircle, UserPlus, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import QuoteSignatureModal from './QuoteSignatureModal';
import ScheduleJobModal from './ScheduleJobModal';
import PostSignatureWorkflow from './PostSignatureWorkflow';
import { generateRecurringInstances } from './recurringJobUtils';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import { formatDisplayDate } from './dateUtils';

export default function QuoteBuilder({ companyId, onNavigate, prefilledCustomer, customerId, jobId, userRole }) {
  const { colors } = useTheme();
  const { showAlert, showConfirm, showSuccess, showError } = useModal();
  // State for data from Supabase
  const [customers, setCustomers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [pricingTables, setPricingTables] = useState([]);
  const [pricingTableItems, setPricingTableItems] = useState({}); // { tableId: { skuId: config } }
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);

  // Tech Permissions (loaded from company settings)
  const [techPermissions, setTechPermissions] = useState({
    tech_quoting_mode: 'flexible',
    tech_can_add_manual_items: true,
    tech_can_modify_prices: true,
    tech_max_discount_percent: 100
  });

  // Check if user is a tech (not owner/admin)
  const isTech = userRole === 'field_tech' || userRole === 'member';
  const isAdmin = userRole === 'owner' || userRole === 'admin';

  // Permission helper functions
  const canAddManualItems = () => isAdmin || techPermissions.tech_can_add_manual_items;
  const canModifyPrices = () => isAdmin || techPermissions.tech_can_modify_prices;
  const isStrictMode = () => isTech && techPermissions.tech_quoting_mode === 'strict';

  // Manual price entry state
  const [showManualPriceModal, setShowManualPriceModal] = useState(false);
  const [pendingSku, setPendingSku] = useState(null);
  const [manualPrice, setManualPrice] = useState('');

  // Custom line item state (for manual entry mode)
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [customItemDescription, setCustomItemDescription] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');
  const [customItemQuantity, setCustomItemQuantity] = useState('1');

  // UI State
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'signed', 'archived'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPricingTable, setSelectedPricingTable] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [showSkuPicker, setShowSkuPicker] = useState(false);
  const [skuSearch, setSkuSearch] = useState('');
  const [customerPreview, setCustomerPreview] = useState(false);
  const [taxRate, setTaxRate] = useState(0.08);
  const [showNewQuote, setShowNewQuote] = useState(prefilledCustomer ? true : false); // Auto-open if prefilled
  const [showQuotesList, setShowQuotesList] = useState(prefilledCustomer ? false : true); // Hide list if prefilled
  const [editingQuote, setEditingQuote] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [selectedQuoteForSigning, setSelectedQuoteForSigning] = useState(null);
  const [signingCustomer, setSigningCustomer] = useState(null);
  const [signingQuoteItems, setSigningQuoteItems] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedQuoteForScheduling, setSelectedQuoteForScheduling] = useState(null);
  const [showPostSignatureWorkflow, setShowPostSignatureWorkflow] = useState(false);
  const [signedQuote, setSignedQuote] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [quoteTermsMode, setQuoteTermsMode] = useState('optional_on');
  const [salesTaxMode, setSalesTaxMode] = useState('optional'); // 'required', 'optional', 'disabled'
  const [advancedQuotingEnabled, setAdvancedQuotingEnabled] = useState(false);
  const [businessType, setBusinessType] = useState(null);
  const [inventoryStockBySkuId, setInventoryStockBySkuId] = useState({}); // { skuId: { qty, itemName } }
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email', 'sms', 'both'
  const [quoteLinkCopied, setQuoteLinkCopied] = useState(false);
  // Send modal editable fields
  const [sendModalEmail, setSendModalEmail] = useState('');
  const [sendModalPhone, setSendModalPhone] = useState('');
  const [editingContactInfo, setEditingContactInfo] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState([]); // [{email: '', phone: ''}]

  // Build initial quote details - prefill from customer if provided
  const getInitialQuoteDetails = () => {
    if (prefilledCustomer) {
      const name = prefilledCustomer.name ||
        `${prefilledCustomer.first_name || ''} ${prefilledCustomer.last_name || ''}`.trim();
      const address = [
        prefilledCustomer.address,
        prefilledCustomer.city,
        prefilledCustomer.state,
        prefilledCustomer.zip
      ].filter(Boolean).join(', ');

      return {
        customer_id: prefilledCustomer.id,
        customerName: name,
        customerEmail: prefilledCustomer.email || '',
        customerPhone: prefilledCustomer.phone || '',
        customerAddress: address,
        notes: '',
        status: 'draft',
        deposit_required: false,
        deposit_amount: 0,
        deposit_percentage: 50,
        include_terms: true,
        // Recurring service options
        is_recurring: false,
        recurrence_rule: 'weekly',
        recurrence_end_date: null
      };
    }
    return {
      customer_id: null,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerAddress: '',
      notes: '',
      status: 'draft',
      deposit_required: false,
      deposit_amount: 0,
      deposit_percentage: 50,
      include_terms: true,
      // Recurring service options
      is_recurring: false,
      recurrence_rule: 'weekly',
      recurrence_end_date: null
    };
  };

  const [quoteDetails, setQuoteDetails] = useState(getInitialQuoteDetails);

  // Ref for product dropdown click-outside detection
  const productDropdownRef = useRef(null);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowSkuPicker(false);
      }
    };

    if (showSkuPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSkuPicker]);

  // Load initial data
  useEffect(() => {
    if (companyId) {
      loadAllData();
    } else {
      setLoading(false);
    }

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [companyId]);

  // Load customer by ID if customerId is provided (e.g., from "Build Quote" button on a visit)
  useEffect(() => {
    if (customerId && !prefilledCustomer && !loading) {
      loadCustomerById(customerId);
    }
  }, [customerId, loading]);

  const loadCustomerById = async (custId) => {
    try {
      const { data: customer, error } = await supabaseData
        .from('customers')
        .select('*')
        .eq('id', custId)
        .single();

      if (error) {
        console.error('Failed to load customer:', error);
        return;
      }

      if (customer) {
        const name = customer.name ||
          `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
        const address = [
          customer.address,
          customer.city,
          customer.state,
          customer.zip
        ].filter(Boolean).join(', ');

        // Set up quote details with this customer
        setQuoteDetails({
          customer_id: customer.id,
          customerName: name,
          customerEmail: customer.email || '',
          customerPhone: customer.phone || '',
          customerAddress: address,
          notes: '',
          status: 'draft',
          deposit_required: false,
          deposit_amount: 0,
          deposit_percentage: 50,
          include_terms: true,
          is_recurring: false,
          recurrence_rule: 'weekly',
          recurrence_end_date: null
        });

        // Auto-open new quote form and hide list
        setShowNewQuote(true);
        setShowQuotesList(false);
      }
    } catch (err) {
      console.error('Error loading customer:', err);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchCustomers(),
        fetchSkus(),
        fetchPricingTables(),
        fetchQuotes(),
        fetchCompanySettings(),
        fetchPricingTableItems(),
        fetchInventoryStock()
      ]);
    } catch (error) {
      showError('Failed to load initial data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingTableItems = async () => {
    try {
      const { data, error } = await supabaseData
        .from('pricing_table_items')
        .select('*');

      if (error) {
        // Table might not exist yet
        return;
      }

      // Organize by pricing_table_id -> sku_id -> config
      const itemsMap = {};
      (data || []).forEach(item => {
        if (!itemsMap[item.pricing_table_id]) {
          itemsMap[item.pricing_table_id] = {};
        }
        itemsMap[item.pricing_table_id][item.sku_id] = {
          price_mode: item.price_mode,
          fixed_price: item.fixed_price,
          multiplier_override: item.multiplier_override,
          is_hidden: item.is_hidden
        };
      });
      setPricingTableItems(itemsMap);
    } catch (error) {
    }
  };

  const fetchCustomers = async () => {
    if (!companyId) return;
    try {
      const { data, error } = await supabaseData
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      
      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
    }
  };

  const fetchSkus = async () => {
    try {
      const { data, error } = await supabaseData
        .from('skus')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (error) throw error;
      setSkus(data || []);
    } catch (error) {
    }
  };

  const fetchPricingTables = async () => {
    try {
      const { data, error } = await supabaseData
        .from('pricing_tables')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (error) throw error;
      setPricingTables(data || []);
    } catch (error) {
    }
  };

  const fetchQuotes = async () => {
    if (!companyId) return;
    try {
      const { data, error } = await supabaseData
        .from('quotes')
        .select(`
          *,
          customer:customers(name, email, phone),
          job:jobs(id, job_number, status, scheduled_date)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
    }
  };

  const fetchCompanySettings = async () => {
    if (!companyId) return;
    try {
      const { data, error } = await supabaseData
        .from('companies')
        .select('name, quote_company_name, recurring_enabled, quote_terms_mode, tech_quoting_mode, tech_can_add_manual_items, tech_can_modify_prices, tech_max_discount_percent, sales_tax_mode, advanced_quoting_enabled, business_type')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(data);
      setRecurringEnabled(data?.recurring_enabled || false);
      setQuoteTermsMode(data?.quote_terms_mode || 'optional_on');
      setSalesTaxMode(data?.sales_tax_mode || 'optional');
      setAdvancedQuotingEnabled(data?.advanced_quoting_enabled || false);
      setBusinessType(data?.business_type || null);

      // Set tech permissions
      setTechPermissions({
        tech_quoting_mode: data?.tech_quoting_mode || 'flexible',
        tech_can_add_manual_items: data?.tech_can_add_manual_items ?? true,
        tech_can_modify_prices: data?.tech_can_modify_prices ?? true,
        tech_max_discount_percent: data?.tech_max_discount_percent ?? 100
      });
    } catch (error) {
    }
  };

  const fetchInventoryStock = async () => {
    if (!companyId) return;
    try {
      // Load inventory items linked to SKUs with their stock levels
      const { data: invItems } = await supabaseData
        .from('inventory_items')
        .select('id, sku_id, name')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .not('sku_id', 'is', null);

      if (!invItems || invItems.length === 0) return;

      // Get default location
      const { data: defaultLoc } = await supabaseData
        .from('locations')
        .select('id')
        .eq('company_id', companyId)
        .eq('is_default', true)
        .single();

      if (!defaultLoc) return;

      const invItemIds = invItems.map(i => i.id);
      const { data: stockData } = await supabaseData
        .from('inventory_stock')
        .select('inventory_item_id, quantity_on_hand')
        .eq('location_id', defaultLoc.id)
        .in('inventory_item_id', invItemIds);

      const stockMap = {};
      (stockData || []).forEach(s => { stockMap[s.inventory_item_id] = s.quantity_on_hand || 0; });

      const bySkuId = {};
      invItems.forEach(inv => {
        bySkuId[inv.sku_id] = {
          qty: stockMap[inv.id] || 0,
          itemName: inv.name
        };
      });
      setInventoryStockBySkuId(bySkuId);
    } catch (error) {
      // Non-critical — inventory stock badges are optional
    }
  };

  // Archive/Unarchive quote
  const toggleArchiveQuote = async (quoteId, currentArchived) => {
    try {
      const { error } = await supabaseData
        .from('quotes')
        .update({ archived: !currentArchived })
        .eq('id', quoteId);

      if (error) throw error;
      
      fetchQuotes();
    } catch (error) {
      showError('Failed to archive quote');
    }
  };

  // Delete quote
  const deleteQuote = async (quoteId) => {
    const confirmed = await showConfirm('Are you sure you want to permanently delete this quote?', {
      title: 'Delete Quote',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('quotes')
        .delete()
        .eq('id', quoteId);

      if (error) throw error;

      fetchQuotes();
    } catch (error) {
      showError('Failed to delete quote');
    }
  };

  // Filter quotes based on active tab and search
  const getFilteredQuotes = () => {
    let filtered = quotes;

    // Filter by tab
    if (activeTab === 'active') {
      filtered = filtered.filter(q => !q.archived && q.status !== 'signed');
    } else if (activeTab === 'signed') {
      filtered = filtered.filter(q => !q.archived && q.status === 'signed');
    } else if (activeTab === 'archived') {
      filtered = filtered.filter(q => q.archived);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const selectCustomer = (customer) => {
    setQuoteDetails({
      ...quoteDetails,
      customer_id: customer.id,
      customerName: customer.name,
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      customerAddress: `${customer.address_street || ''}, ${customer.address_city || ''}, ${customer.address_state || ''} ${customer.address_zip || ''}`.trim()
    });
    setCustomerPreview(false);
  };

  // Get the price for an SKU based on pricing table settings and per-SKU overrides
  const getSkuPrice = (sku, pricingTable) => {
    const basePrice = sku.base_cost || sku.cost || 0;
    const tableMultiplier = pricingTable.multiplier || 1;

    // Check for per-SKU configuration
    const skuConfig = pricingTableItems[pricingTable.id]?.[sku.id];

    // If SKU is hidden for this table, return null (shouldn't be added)
    if (skuConfig?.is_hidden) {
      return { price: null, isManual: false, isHidden: true };
    }

    // Determine price mode: per-SKU override > table default
    let priceMode = skuConfig?.price_mode || 'adjusted';

    // If table is in manual mode and no SKU override, use manual
    if (pricingTable.pricing_mode === 'manual' && !skuConfig?.price_mode) {
      priceMode = 'manual';
    }

    switch (priceMode) {
      case 'base':
        return { price: basePrice, isManual: false, isHidden: false };

      case 'adjusted':
        const mult = skuConfig?.multiplier_override || tableMultiplier;
        return { price: basePrice * mult, isManual: false, isHidden: false };

      case 'fixed':
        const fixedPrice = skuConfig?.fixed_price;
        if (fixedPrice !== null && fixedPrice !== undefined) {
          return { price: fixedPrice, isManual: false, isHidden: false };
        }
        // Fallback to adjusted if no fixed price set
        return { price: basePrice * tableMultiplier, isManual: false, isHidden: false };

      case 'manual':
        return { price: null, isManual: true, isHidden: false };

      default:
        return { price: basePrice * tableMultiplier, isManual: false, isHidden: false };
    }
  };

  const addItem = (sku) => {
    // Use the SKU's price directly - check price, base_price, and base_cost fields
    const price = parseFloat(sku.price) || parseFloat(sku.base_price) || parseFloat(sku.base_cost) || 0;

    const newItem = {
      id: Date.now(),
      sku_id: sku.id,
      name: sku.name,
      quantity: 1,
      unit_price: price,
      total_price: price
    };

    setQuoteItems([...quoteItems, newItem]);
    setShowSkuPicker(false);
    setSkuSearch('');
  };

  const confirmManualPrice = () => {
    if (!pendingSku) return;

    const price = parseFloat(manualPrice) || 0;
    if (price <= 0) {
      showAlert('Please enter a valid price', { title: 'Invalid Price' });
      return;
    }

    const newItem = {
      id: Date.now(),
      sku_id: pendingSku.id,
      name: pendingSku.name,
      quantity: 1,
      unit_price: price,
      total_price: price
    };

    setQuoteItems([...quoteItems, newItem]);
    setShowManualPriceModal(false);
    setPendingSku(null);
    setManualPrice('');
    setShowSkuPicker(false);
    setSkuSearch('');
  };

  // Add a custom line item (manual entry mode)
  const addCustomItem = () => {
    // Check permission
    if (!canAddManualItems()) {
      showAlert('You do not have permission to add custom items. Please select from the price list.', { title: 'Permission Denied' });
      return;
    }

    const description = customItemDescription.trim();
    const price = parseFloat(customItemPrice) || 0;
    const quantity = parseInt(customItemQuantity) || 1;

    if (!description) {
      showAlert('Please enter a description', { title: 'Description Required' });
      return;
    }
    if (price <= 0) {
      showAlert('Please enter a valid price', { title: 'Price Required' });
      return;
    }

    const newItem = {
      id: Date.now(),
      sku_id: null, // No SKU for manual items
      name: description,
      quantity: quantity,
      unit_price: price,
      total_price: price * quantity
    };

    setQuoteItems([...quoteItems, newItem]);
    setShowCustomItemModal(false);
    setCustomItemDescription('');
    setCustomItemPrice('');
    setCustomItemQuantity('1');
  };

  const updateQuantity = (itemId, quantity) => {
    const qty = Math.max(1, parseInt(quantity) || 1);
    setQuoteItems(quoteItems.map(item =>
      item.id === itemId
        ? { ...item, quantity: qty, total_price: item.unit_price * qty }
        : item
    ));
  };

  const updatePrice = (itemId, price) => {
    // Check permission
    if (!canModifyPrices()) {
      showAlert('You do not have permission to modify prices.', { title: 'Permission Denied' });
      return;
    }

    const p = Math.max(0, parseFloat(price) || 0);
    setQuoteItems(quoteItems.map(item =>
      item.id === itemId
        ? { ...item, unit_price: p, total_price: p * item.quantity }
        : item
    ));
  };

  const removeItem = (itemId) => {
    setQuoteItems(quoteItems.filter(item => item.id !== itemId));
  };

  const calculateSubtotal = () => {
    return quoteItems.reduce((sum, item) => sum + item.total_price, 0);
  };

  const calculateTax = () => {
    if (salesTaxMode === 'disabled') return 0;
    return calculateSubtotal() * taxRate;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const saveQuote = async () => {
    if (!quoteDetails.customer_id) {
      showAlert('Please select a customer', { title: 'Customer Required' });
      return;
    }

    if (quoteItems.length === 0) {
      showAlert('Please add at least one item', { title: 'Items Required' });
      return;
    }

    try {
      const quoteData = {
        company_id: companyId,
        customer_id: quoteDetails.customer_id,
        pricing_table_id: null, // Simplified - no pricing tables
        subtotal: calculateSubtotal(),
        tax_rate: taxRate,
        total: calculateTotal(),
        notes: quoteDetails.notes,
        status: quoteDetails.status,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        deposit_required: quoteDetails.deposit_required || false,
        deposit_amount: quoteDetails.deposit_required ? (quoteDetails.deposit_amount || 0) : 0,
        include_terms: quoteDetails.include_terms !== false,
        // Recurring service options
        is_recurring: quoteDetails.is_recurring || false,
        recurrence_rule: quoteDetails.is_recurring ? quoteDetails.recurrence_rule : null,
        recurrence_end_date: quoteDetails.is_recurring ? quoteDetails.recurrence_end_date : null
      };

      let savedQuote;

      if (editingQuote) {
        const { data, error } = await supabaseData
          .from('quotes')
          .update(quoteData)
          .eq('id', editingQuote.id)
          .select()
          .single();

        if (error) throw error;
        savedQuote = data;

        await supabaseData
          .from('quote_items')
          .delete()
          .eq('quote_id', editingQuote.id);
      } else {
        const { data, error } = await supabaseData
          .from('quotes')
          .insert([quoteData])
          .select()
          .single();

        if (error) throw error;
        savedQuote = data;
      }

      const quoteItemsData = quoteItems.map(item => ({
        quote_id: savedQuote.id,
        sku_id: item.sku_id,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      }));

      const { error: itemsError } = await supabaseData
        .from('quote_items')
        .insert(quoteItemsData);

      if (itemsError) throw itemsError;

      return savedQuote; // Return saved quote for chaining
    } catch (error) {
      showError('Failed to save quote: ' + error.message);
      return null;
    }
  };

  // Wrapper for regular save (shows alert and resets)
  const handleSaveQuote = async () => {
    const saved = await saveQuote();
    if (saved) {
      showSuccess('Quote saved successfully!');
      resetForm();
      fetchQuotes();
    }
  };

  // Save and immediately open signature modal
  const saveAndSign = async () => {
    const saved = await saveQuote();
    if (saved) {
      await fetchQuotes(); // Refresh quotes list
      prepareQuoteForSigning(saved);
    }
  };

  // Save and send quote for remote signing
  const saveAndSend = async () => {
    const saved = await saveQuote();
    if (saved) {
      await fetchQuotes();
      setEditingQuote(saved);
      // Initialize send modal fields from customer
      const customer = customers.find(c => c.id === saved.customer_id);
      if (customer) {
        setSendModalEmail(customer.email || '');
        setSendModalPhone(customer.phone || '');
      }
      setEditingContactInfo(false);
      setAdditionalRecipients([]);
      setShowSendModal(true);
    }
  };

  // Open send modal for an existing quote (resend)
  const openSendModalForQuote = (quote) => {
    setEditingQuote(quote);
    const customer = customers.find(c => c.id === quote.customer_id);
    if (customer) {
      setSendModalEmail(customer.email || '');
      setSendModalPhone(customer.phone || '');
    }
    setEditingContactInfo(false);
    setAdditionalRecipients([]);
    setShowSendModal(true);
  };

  // Get the quote URL for remote signing
  const getQuoteUrl = (quoteId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/quote/${quoteId}`;
  };

  // Copy quote link to clipboard
  const copyQuoteLink = async () => {
    if (!editingQuote?.id) return;
    const url = getQuoteUrl(editingQuote.id);
    try {
      await navigator.clipboard.writeText(url);
      setQuoteLinkCopied(true);
      setTimeout(() => setQuoteLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setQuoteLinkCopied(true);
      setTimeout(() => setQuoteLinkCopied(false), 2000);
    }
  };

  // Send quote via email/SMS
  const handleSendQuote = async () => {
    if (!editingQuote?.id) return;

    const customer = customers.find(c => c.id === editingQuote.customer_id);
    if (!customer) {
      showError('Customer not found');
      return;
    }

    // Use the editable modal fields instead of customer record
    const primaryEmail = sendModalEmail?.trim();
    const primaryPhone = sendModalPhone?.trim();

    const quoteUrl = getQuoteUrl(editingQuote.id);
    const companyName = company?.quote_company_name || company?.name || 'Our Company';

    // Build list of all recipients (primary + additional)
    const allEmails = [primaryEmail, ...additionalRecipients.map(r => r.email?.trim())].filter(Boolean);
    const allPhones = [primaryPhone, ...additionalRecipients.map(r => r.phone?.trim())].filter(Boolean);

    setSendingQuote(true);
    try {
      let emailsSent = 0;
      let smsSent = 0;

      // Update customer record if contact info was changed
      if (primaryEmail !== customer.email || primaryPhone !== customer.phone) {
        const updateData = {};
        if (primaryEmail && primaryEmail !== customer.email) updateData.email = primaryEmail;
        if (primaryPhone && primaryPhone !== customer.phone) updateData.phone = primaryPhone;
        if (Object.keys(updateData).length > 0) {
          await supabaseData
            .from('customers')
            .update(updateData)
            .eq('id', customer.id);
        }
      }

      // Send emails if selected
      if (sendMethod === 'email' || sendMethod === 'both') {
        for (const email of allEmails) {
          const emailResponse = await fetch('/api/email-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: email,
              subject: `Quote from ${companyName} - ${editingQuote.quote_number}`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #1a1a1a;">You have a new quote from ${companyName}</h2>
                  <p style="color: #666;">Hi ${customer.first_name || customer.name || 'there'},</p>
                  <p style="color: #666;">Please review and sign your quote by clicking the button below:</p>
                  <div style="text-align: center; margin: 2rem 0;">
                    <a href="${quoteUrl}" style="background: #F05A28; color: white; padding: 1rem 2rem; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                      View & Sign Quote
                    </a>
                  </div>
                  <p style="color: #999; font-size: 0.9rem;">Or copy this link: ${quoteUrl}</p>
                  <hr style="border: none; border-top: 1px solid #eee; margin: 2rem 0;" />
                  <p style="color: #999; font-size: 0.85rem;">This quote was sent via Work Van</p>
                </div>
              `,
              companyId: companyId
            })
          });
          if (emailResponse.ok) emailsSent++;
        }
      }

      // Send SMS if selected
      if (sendMethod === 'sms' || sendMethod === 'both') {
        for (const phone of allPhones) {
          const smsResponse = await fetch('/api/sms-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: phone,
              message: `${companyName}: You have a new quote ready for review. View and sign here: ${quoteUrl}`,
              customerId: customer.id,
              companyId: companyId
            })
          });
          if (smsResponse.ok) smsSent++;
        }
      }

      // Update quote status to 'sent'
      await supabaseData
        .from('quotes')
        .update({ status: 'sent', sent_at: new Date().toISOString() })
        .eq('id', editingQuote.id);

      if (emailsSent > 0 || smsSent > 0) {
        const methods = [];
        if (emailsSent > 0) methods.push(`${emailsSent} email${emailsSent > 1 ? 's' : ''}`);
        if (smsSent > 0) methods.push(`${smsSent} SMS`);
        showSuccess(`Quote sent via ${methods.join(' and ')}!`);
        setShowSendModal(false);
        resetForm();
        fetchQuotes();
        // Refresh customers list to show updated contact info
        fetchCustomers();
      } else {
        showError('Failed to send quote. Please add an email or phone number.');
      }
    } catch (err) {
      showError('Failed to send quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const resetForm = () => {
    setQuoteDetails({
      customer_id: null,
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerAddress: '',
      notes: '',
      status: 'draft',
      include_terms: quoteTermsMode === 'optional_on' || quoteTermsMode === 'required'
    });
    setQuoteItems([]);
    setSelectedPricingTable(null);
    setShowNewQuote(false);
    setShowQuotesList(true);
    setEditingQuote(null);
  };

  const editQuote = async (quote) => {
    try {
      const { data: items, error } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id);

      if (error) throw error;

      const customer = customers.find(c => c.id === quote.customer_id);
      
      setQuoteDetails({
        customer_id: quote.customer_id,
        customerName: customer?.name || '',
        customerEmail: customer?.email || '',
        customerPhone: customer?.phone || '',
        customerAddress: `${customer?.address_street || ''}, ${customer?.address_city || ''}, ${customer?.address_state || ''} ${customer?.address_zip || ''}`.trim(),
        notes: quote.notes || '',
        status: quote.status,
        deposit_required: quote.deposit_required || false,
        deposit_amount: quote.deposit_amount || 0,
        deposit_percentage: quote.total > 0 ? Math.round((quote.deposit_amount || 0) / quote.total * 100) : 50,
        include_terms: quote.include_terms !== false,
        // Recurring service options
        is_recurring: quote.is_recurring || false,
        recurrence_rule: quote.recurrence_rule || 'weekly',
        recurrence_end_date: quote.recurrence_end_date || null
      });

      setQuoteItems((items || []).map(item => ({
        id: item.id,
        sku_id: item.sku_id,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      })));

      setSelectedPricingTable(quote.pricing_table_id || 'manual');
      setTaxRate(quote.tax_rate || 0.08);
      setEditingQuote(quote);
      setShowNewQuote(true);
      setShowQuotesList(false);
    } catch (error) {
      showError('Failed to load quote');
    }
  };

  const prepareQuoteForSigning = async (quote) => {
    try {
      // Fetch customer
      const { data: customer, error: customerError } = await supabaseData
        .from('customers')
        .select('*')
        .eq('id', quote.customer_id)
        .single();

      if (customerError) throw customerError;

      // Fetch quote items
      const { data: items, error: itemsError } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id);

      if (itemsError) throw itemsError;

      setSelectedQuoteForSigning(quote);
      setSigningCustomer(customer);
      setSigningQuoteItems(items || []);
      setShowSignModal(true);
    } catch (error) {
      showError('Failed to load quote details');
    }
  };

  const viewSignedPDF = (quote) => {
    if (quote.signed_pdf_url) {
      window.open(quote.signed_pdf_url, '_blank');
    }
  };

  const downloadQuotePDF = async (quote) => {
    try {
      // Fetch quote items
      const { data: items } = await supabaseData
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id)
        .order('created_at');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;
      const companyName = company?.quote_company_name || company?.name || 'Company';

      // Company header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName, 20, y);
      y += 12;

      // Quote title
      doc.setFontSize(14);
      doc.text(`Quote #${quote.quote_number}`, 20, y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const statusText = (quote.status || 'draft').charAt(0).toUpperCase() + (quote.status || 'draft').slice(1);
      doc.text(`Status: ${statusText}`, pageWidth - 20, y, { align: 'right' });
      y += 8;

      // Date
      doc.setFontSize(10);
      if (quote.created_at) {
        doc.text(`Date: ${new Date(quote.created_at).toLocaleDateString()}`, 20, y);
      }
      if (quote.expires_at) {
        doc.text(`Expires: ${new Date(quote.expires_at).toLocaleDateString()}`, pageWidth - 20, y, { align: 'right' });
      }
      y += 12;

      // Customer info
      doc.setFont('helvetica', 'bold');
      doc.text('Customer', 20, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(quote.customerName || quote.customers?.name || 'N/A', 20, y);
      y += 5;
      if (quote.customers?.email) { doc.text(quote.customers.email, 20, y); y += 5; }
      if (quote.customers?.phone) { doc.text(quote.customers.phone, 20, y); y += 5; }
      y += 8;

      // Line items table
      if (items && items.length > 0) {
        // Table header
        doc.setFillColor(50, 50, 50);
        doc.rect(20, y, pageWidth - 40, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Item', 22, y + 5.5);
        doc.text('Qty', pageWidth - 75, y + 5.5);
        doc.text('Price', pageWidth - 55, y + 5.5);
        doc.text('Total', pageWidth - 30, y + 5.5, { align: 'right' });
        y += 10;

        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        items.forEach((item) => {
          if (y > 260) { doc.addPage(); y = 20; }
          const qty = item.quantity || 1;
          const price = item.unit_price || item.price || 0;
          const total = qty * price;
          doc.text(item.name || item.description || 'Item', 22, y + 4);
          doc.text(String(qty), pageWidth - 75, y + 4);
          doc.text(`$${price.toFixed(2)}`, pageWidth - 55, y + 4);
          doc.text(`$${total.toFixed(2)}`, pageWidth - 30, y + 4, { align: 'right' });
          doc.setDrawColor(220, 220, 220);
          doc.line(20, y + 7, pageWidth - 20, y + 7);
          y += 9;
        });
        y += 4;
      }

      // Total
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`Total: $${(quote.total || 0).toFixed(2)}`, pageWidth - 30, y, { align: 'right' });
      y += 8;

      // Notes
      if (quote.notes) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text('Notes:', 20, y);
        y += 5;
        const noteLines = doc.splitTextToSize(quote.notes, pageWidth - 40);
        doc.text(noteLines, 20, y);
      }

      doc.save(`Quote-${quote.quote_number}.pdf`);
      showSuccess('Quote PDF downloaded!');
    } catch (error) {
      showError('Failed to download quote PDF: ' + error.message);
    }
  };

  const emailSignedQuote = async (quote) => {
    showAlert('Email functionality is not yet available. For now, you can download the PDF and email it manually.', {
      title: 'Download PDF Instead',
      icon: 'info'
    });
  };

  const scheduleJobFromQuote = async (scheduledDate, scheduledTime, estimatedDuration = 60, recurringOptions = {}) => {
    if (!selectedQuoteForScheduling) return;

    try {
      // Generate job number
      const { data: jobNumberData, error: seqError } = await supabaseData
        .rpc('get_next_job_number');

      let jobNumber;
      if (seqError) {
        // Fallback to timestamp-based number
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const time = String(date.getTime()).slice(-6);
        jobNumber = `JOB-${year}${month}${day}-${time}`;
      } else {
        jobNumber = jobNumberData;
      }

      // Create job from quote with recurring options
      const jobData = {
        company_id: companyId,
        job_number: jobNumber,
        customer_id: selectedQuoteForScheduling.customer_id,
        quote_id: selectedQuoteForScheduling.id,
        job_type: 'repair',
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime,
        estimated_duration: parseFloat(estimatedDuration) || 2,
        status: 'scheduled',
        quoted_value: parseFloat(selectedQuoteForScheduling.total) || 0,
        pricing_table_id: selectedQuoteForScheduling.pricing_table_id,
        deposit_required: selectedQuoteForScheduling.deposit_required || false,
        deposit_amount: selectedQuoteForScheduling.deposit_amount || 0,
        deposit_paid: selectedQuoteForScheduling.deposit_paid || false,
        deposit_paid_at: selectedQuoteForScheduling.deposit_paid ? (selectedQuoteForScheduling.deposit_paid_at || new Date().toISOString()) : null,
        // Recurring fields
        is_recurring: recurringOptions.is_recurring || false,
        recurrence_rule: recurringOptions.recurrence_rule || null,
        recurrence_end_date: recurringOptions.recurrence_end_date || null
      };

      const { data: job, error: jobError } = await supabaseData
        .from('jobs')
        .insert([jobData])
        .select()
        .single();

      if (jobError) {
        throw jobError;
      }

      // Update quote status to 'scheduled' to prevent double-scheduling
      const { error: quoteUpdateError } = await supabaseData
        .from('quotes')
        .update({ status: 'scheduled' })
        .eq('id', selectedQuoteForScheduling.id);

      if (quoteUpdateError) {
        // Don't throw - job was created successfully
      }

      // Generate recurring instances if this is a recurring job
      let instanceCount = 0;
      if (recurringOptions.is_recurring && job) {
        const result = await generateRecurringInstances(job);
        if (result.success) {
          instanceCount = result.instances.length;
        } else {
        }
      }

      const recurringMsg = recurringOptions.is_recurring
        ? ` (Recurring ${recurringOptions.recurrence_rule} - ${instanceCount + 1} total jobs created)`
        : '';
      showSuccess(`Job ${jobNumber} scheduled successfully for ${formatDisplayDate(scheduledDate)} at ${scheduledTime}!${recurringMsg}`);
      setShowScheduleModal(false);
      setSelectedQuoteForScheduling(null);

      // Reload quotes to reflect the status change
      loadAllData();
    } catch (error) {
      throw error;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return colors.textMuted;
      case 'sent': return '#45b7d1';
      case 'signed': return colors.success;
      case 'scheduled': return '#2a9d8f';
      case 'expired': return '#ff6b6b';
      default: return colors.textMuted;
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const filteredQuotes = getFilteredQuotes();
  const tabCounts = {
    active: quotes.filter(q => !q.archived && q.status !== 'signed').length,
    signed: quotes.filter(q => !q.archived && q.status === 'signed').length,
    archived: quotes.filter(q => q.archived).length
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: `3px solid ${colors.border}`,
          borderTop: `3px solid ${colors.accent}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '1rem' : '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
      background: colors.bg,
      minHeight: '100vh'
    }}>
      {showQuotesList && (
        <>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            marginBottom: '2rem',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '1rem'
          }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? '1.75rem' : '2rem', color: colors.text }}>
              📋 Quote Builder
            </h1>
            <button
              onClick={() => {
                resetForm();
                setShowNewQuote(true);
                setShowQuotesList(false);
              }}
              style={{
                background: colors.accent,
                color: '#fff',
                border: 'none',
                padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <Plus size={20} />
              New Quote
            </button>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '0.5rem' : '1rem',
            marginBottom: '1.5rem',
            borderBottom: `2px solid ${colors.border}`,
            overflowX: 'auto',
            paddingBottom: '1px'
          }}>
            {[
              { key: 'active', label: 'Active', icon: '🟢' },
              { key: 'signed', label: 'Signed', icon: '✅' },
              { key: 'archived', label: 'Archived', icon: '📁' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: activeTab === tab.key ? colors.accent : colors.textMuted,
                  padding: isMobile ? '0.75rem 1rem' : '0.75rem 1.5rem',
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                  cursor: 'pointer',
                  borderBottom: activeTab === tab.key ? `3px solid ${colors.accent}` : '3px solid transparent',
                  marginBottom: '-2px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span style={{
                  background: activeTab === tab.key ? colors.accent : colors.border,
                  color: activeTab === tab.key ? '#fff' : colors.textMuted,
                  padding: '0.125rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {tabCounts[tab.key]}
                </span>
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div style={{
            marginBottom: '1.5rem',
            position: 'relative'
          }}>
            <Search
              size={20}
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted
              }}
            />
            <input
              type="text"
              placeholder="Search by customer name or quote number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                outline: 'none'
              }}
            />
          </div>

          {/* Quotes List */}
          {filteredQuotes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              background: colors.bgCard,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`
            }}>
              <FileText size={64} style={{ color: colors.border, marginBottom: '1rem' }} />
              <h3 style={{ margin: '0 0 0.5rem 0', color: colors.textMuted }}>
                {searchTerm ? 'No quotes found' : `No ${activeTab} quotes`}
              </h3>
              <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                {searchTerm ? 'Try a different search term' : 'Create your first quote to get started'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: isMobile ? '1rem' : '1.5rem',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))'
            }}>
              {filteredQuotes.map(quote => (
                <div
                  key={quote.id}
                  onClick={() => editQuote(quote)}
                  style={{
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                    padding: isMobile ? '1.25rem' : '1.5rem',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#4a4a4a';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Quote Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: colors.textMuted,
                        fontFamily: 'monospace',
                        marginBottom: '0.25rem'
                      }}>
                        {quote.quote_number}
                      </div>
                      <h3 style={{
                        margin: 0,
                        fontSize: '1.25rem',
                        color: colors.text
                      }}>
                        {quote.customer?.name || 'Unknown Customer'}
                      </h3>
                    </div>
                    <div style={{
                      background: getStatusColor(quote.status),
                      color: '#fff',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase'
                    }}>
                      {getStatusLabel(quote.status)}
                    </div>
                  </div>

                  {/* Quote Details */}
                  <div style={{
                    marginBottom: '1rem',
                    paddingBottom: '1rem',
                    borderBottom: `1px solid ${colors.border}`
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: colors.success,
                      marginBottom: '0.5rem'
                    }}>
                      ${quote.total?.toFixed(2) || '0.00'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                      Created: {formatDisplayDate(quote.created_at?.split('T')[0])}
                    </div>
                    {quote.expires_at && (
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                        Expires: {formatDisplayDate(quote.expires_at?.split('T')[0])}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    {(quote.status === 'signed' || quote.status === 'scheduled') && quote.signed_pdf_url ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            viewSignedPDF(quote);
                          }}
                          style={{
                            flex: 1,
                            minWidth: '100px',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: colors.text,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <Eye size={16} />
                          View PDF
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            emailSignedQuote(quote);
                          }}
                          style={{
                            flex: 1,
                            minWidth: '100px',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: colors.text,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          📧 Email
                        </button>
                        {quote.job ? (
                          <div style={{
                            flex: 1,
                            minWidth: '120px',
                            background: '#2a5a4a',
                            border: `1px solid ${colors.success}`,
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: colors.success,
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}>
                            ✓ Job {quote.job.job_number}
                          </div>
                        ) : quote.status === 'signed' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedQuoteForScheduling(quote);
                              setShowScheduleModal(true);
                            }}
                            style={{
                              flex: 1,
                              minWidth: '120px',
                              background: colors.success,
                              border: 'none',
                              borderRadius: '6px',
                              padding: '0.5rem',
                              color: '#000',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <Calendar size={16} />
                            Schedule Job
                          </button>
                        ) : (
                          <div style={{
                            flex: 1,
                            minWidth: '120px',
                            background: '#2a5a4a',
                            border: `1px solid ${colors.success}`,
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: colors.success,
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}>
                            ✓ Scheduled
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            editQuote(quote);
                          }}
                          style={{
                            flex: 1,
                            minWidth: '80px',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: colors.text,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                        {/* Download PDF */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadQuotePDF(quote);
                          }}
                          style={{
                            flex: 1,
                            minWidth: '80px',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: colors.text,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <Download size={14} />
                          PDF
                        </button>
                        {/* View Quote (for sent quotes) */}
                        {quote.status === 'sent' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(getQuoteUrl(quote.id), '_blank');
                            }}
                            style={{
                              flex: 1,
                              minWidth: '80px',
                              background: colors.bgInput,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              padding: '0.5rem',
                              color: colors.text,
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <Eye size={14} />
                            View
                          </button>
                        )}
                        {quote.status !== 'signed' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              prepareQuoteForSigning(quote);
                            }}
                            style={{
                              flex: 1,
                              minWidth: '80px',
                              background: colors.success,
                              border: 'none',
                              borderRadius: '6px',
                              padding: '0.5rem',
                              color: '#000',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            ✍️ Sign
                          </button>
                        )}
                        {/* Resend button for sent quotes */}
                        {quote.status === 'sent' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openSendModalForQuote(quote);
                            }}
                            style={{
                              flex: 1,
                              minWidth: '80px',
                              background: `${colors.info || '#3498db'}20`,
                              border: `1px solid ${colors.info || '#3498db'}`,
                              borderRadius: '6px',
                              padding: '0.5rem',
                              color: colors.info || '#3498db',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <Send size={14} />
                            Resend
                          </button>
                        )}
                      </>
                    )}

                    {/* Archive/Unarchive */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleArchiveQuote(quote.id, quote.archived);
                      }}
                      style={{
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        padding: '0.5rem',
                        color: colors.text,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      title={quote.archived ? 'Unarchive' : 'Archive'}
                    >
                      {quote.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </button>

                    {/* Delete */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQuote(quote.id);
                      }}
                      style={{
                        background: colors.error,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.5rem',
                        color: '#fff',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      title="Delete permanently"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* New/Edit Quote Form */}
      {showNewQuote && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <h2 style={{ margin: 0, color: colors.text }}>
              {editingQuote ? 'Edit Quote' : 'New Quote'}
            </h2>
            <button
              onClick={() => {
                resetForm();
                setShowQuotesList(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Customer Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
              Customer *
            </label>
            {quoteDetails.customer_id ? (
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                overflow: 'hidden'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {quoteDetails.customerName}
                    </div>
                    {quoteDetails.customerEmail && (
                      <div style={{ fontSize: '0.9rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quoteDetails.customerEmail}</div>
                    )}
                    {quoteDetails.customerPhone && (
                      <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>{quoteDetails.customerPhone}</div>
                    )}
                  </div>
                  <button
                    onClick={() => setQuoteDetails({ ...quoteDetails, customer_id: null })}
                    style={{
                      background: colors.error,
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.35rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      flexShrink: 0
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                {/* Typeahead Search Input */}
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: colors.textMuted
                  }} />
                  <input
                    type="text"
                    placeholder="Search customers by name, phone, or email..."
                    value={customerPreview ? (typeof customerPreview === 'string' ? customerPreview : '') : ''}
                    onChange={(e) => setCustomerPreview(e.target.value)}
                    onFocus={() => {
                      if (!customerPreview) setCustomerPreview('');
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {/* Filtered Results Dropdown */}
                {customerPreview !== false && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    marginTop: '0.5rem',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                  }}>
                    {(() => {
                      const searchStr = (typeof customerPreview === 'string' ? customerPreview : '').toLowerCase();
                      const filtered = customers.filter(c => {
                        const name = (c.name || `${c.first_name || ''} ${c.last_name || ''}`).toLowerCase();
                        const phone = (c.phone || '').toLowerCase();
                        const email = (c.email || '').toLowerCase();
                        return name.includes(searchStr) || phone.includes(searchStr) || email.includes(searchStr);
                      }).slice(0, 10); // Limit to 10 results

                      if (filtered.length === 0) {
                        return (
                          <div style={{
                            padding: '1rem',
                            color: colors.textMuted,
                            textAlign: 'center'
                          }}>
                            {searchStr ? 'No customers found' : 'Start typing to search...'}
                          </div>
                        );
                      }

                      return filtered.map(customer => (
                        <div
                          key={customer.id}
                          onClick={() => selectCustomer(customer)}
                          style={{
                            padding: '1rem',
                            borderBottom: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'background 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = colors.border}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ fontWeight: 'bold', color: colors.text }}>
                            {customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim()}
                          </div>
                          <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: colors.textMuted }}>
                            {customer.phone && <span>{customer.phone}</span>}
                            {customer.email && <span>{customer.email}</span>}
                          </div>
                        </div>
                      ));
                    })()}

                    {/* Close button at bottom */}
                    <div
                      onClick={() => setCustomerPreview(false)}
                      style={{
                        padding: '0.75rem',
                        textAlign: 'center',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        borderTop: `1px solid ${colors.border}`,
                        fontSize: '0.85rem'
                      }}
                    >
                      Close
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Advanced Quoting Section - Painting */}
          {advancedQuotingEnabled && businessType === 'painting' && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1.25rem',
              background: colors.bgInput,
              borderRadius: '12px',
              border: `2px solid ${colors.accent}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
                color: colors.accent,
                fontWeight: 'bold'
              }}>
                <Palette size={20} />
                Paint Quote Builder
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {/* Interior/Exterior Toggle */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Job Type
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['Interior', 'Exterior', 'Both'].map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setQuoteDetails(prev => ({ ...prev, paint_job_type: type }))}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: quoteDetails.paint_job_type === type ? colors.accent : colors.bgCard,
                          border: `1px solid ${quoteDetails.paint_job_type === type ? colors.accent : colors.border}`,
                          borderRadius: '6px',
                          color: quoteDetails.paint_job_type === type ? '#fff' : colors.text,
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Paint Quality */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Paint Quality
                  </label>
                  <select
                    value={quoteDetails.paint_quality || 'standard'}
                    onChange={(e) => setQuoteDetails(prev => ({ ...prev, paint_quality: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="economy">Economy</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="ultra">Ultra Premium</option>
                  </select>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {/* Square Footage */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Est. Square Footage
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={quoteDetails.paint_sqft || ''}
                    onChange={(e) => setQuoteDetails(prev => ({ ...prev, paint_sqft: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.9rem'
                    }}
                  />
                </div>

                {/* Number of Coats */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Coats
                  </label>
                  <select
                    value={quoteDetails.paint_coats || '2'}
                    onChange={(e) => setQuoteDetails(prev => ({ ...prev, paint_coats: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="1">1 Coat</option>
                    <option value="2">2 Coats</option>
                    <option value="3">3 Coats</option>
                  </select>
                </div>

                {/* Prep Level */}
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Surface Prep
                  </label>
                  <select
                    value={quoteDetails.paint_prep || 'light'}
                    onChange={(e) => setQuoteDetails(prev => ({ ...prev, paint_prep: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="minimal">Minimal</option>
                    <option value="light">Light</option>
                    <option value="moderate">Moderate</option>
                    <option value="extensive">Extensive</option>
                  </select>
                </div>
              </div>

              {/* Room/Area Quick Add */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                  Quick Add Rooms/Areas
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {['Living Room', 'Bedroom', 'Bathroom', 'Kitchen', 'Hallway', 'Exterior Trim', 'Exterior Siding', 'Ceiling', 'Accent Wall'].map(room => (
                    <button
                      key={room}
                      type="button"
                      onClick={() => {
                        const newItem = {
                          id: `room_${Date.now()}`,
                          name: room,
                          description: `${room} - ${quoteDetails.paint_job_type || 'Interior'} painting`,
                          quantity: 1,
                          unit_price: 0,
                          isManual: true
                        };
                        setQuoteItems(prev => [...prev, newItem]);
                      }}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '20px',
                        color: colors.text,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      + {room}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estimate Helper */}
              {quoteDetails.paint_sqft > 0 && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: colors.bgCard,
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  color: colors.textMuted
                }}>
                  <Calculator size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  <strong>{quoteDetails.paint_sqft} sq ft</strong> with <strong>{quoteDetails.paint_coats || 2} coats</strong> =
                  est. <strong>{Math.ceil((quoteDetails.paint_sqft * (quoteDetails.paint_coats || 2)) / 350)} gallons</strong> of paint needed
                </div>
              )}
            </div>
          )}

          {/* Items List */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              <label style={{ color: colors.text, fontWeight: 'bold' }}>Items *</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {/* Add from Catalog - dropdown */}
                <div ref={productDropdownRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      setShowSkuPicker(!showSkuPicker);
                      setSkuSearch('');
                    }}
                    style={{
                      background: colors.success,
                      color: '#000',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Package size={16} />
                    Add Product
                    <ChevronDown size={16} style={{ transform: showSkuPicker ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                  </button>

                  {/* Dropdown */}
                  {showSkuPicker && (
                    <div style={{
                      position: isMobile ? 'fixed' : 'absolute',
                      top: isMobile ? '50%' : '100%',
                      left: isMobile ? '50%' : 'auto',
                      right: isMobile ? 'auto' : 0,
                      transform: isMobile ? 'translate(-50%, -50%)' : 'none',
                      marginTop: isMobile ? 0 : '0.5rem',
                      width: isMobile ? 'calc(100vw - 2rem)' : '320px',
                      maxWidth: '320px',
                      maxHeight: isMobile ? '70vh' : '400px',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      zIndex: 1000,
                      overflow: 'hidden'
                    }}>
                      <div style={{ padding: '0.75rem', borderBottom: `1px solid ${colors.border}` }}>
                        <input
                          type="text"
                          placeholder="Search products..."
                          value={skuSearch}
                          onChange={(e) => setSkuSearch(e.target.value)}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem',
                            outline: 'none'
                          }}
                        />
                      </div>
                      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        {skus
                          .filter(sku => {
                            const matchesSearch = sku.name.toLowerCase().includes(skuSearch.toLowerCase()) ||
                              sku.code?.toLowerCase().includes(skuSearch.toLowerCase());
                            return matchesSearch;
                          })
                          .map(sku => (
                            <div
                              key={sku.id}
                              onClick={() => {
                                addItem(sku);
                                setShowSkuPicker(false);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${colors.border}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'background 0.15s'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = colors.bgInput}
                              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: '500', color: colors.text, fontSize: '0.9rem' }}>
                                  {sku.name}
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                  {sku.code && (
                                    <span style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                                      {sku.code}
                                    </span>
                                  )}
                                  {inventoryStockBySkuId[sku.id] !== undefined && (
                                    <span style={{
                                      fontSize: '0.7rem',
                                      padding: '1px 6px',
                                      borderRadius: '4px',
                                      background: inventoryStockBySkuId[sku.id].qty > 0 ? `${colors.success}20` : `${colors.warning}20`,
                                      color: inventoryStockBySkuId[sku.id].qty > 0 ? colors.success : colors.warning,
                                      fontWeight: 'bold'
                                    }}>
                                      {inventoryStockBySkuId[sku.id].qty} in stock
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ color: colors.success, fontWeight: 'bold', fontSize: '0.9rem', flexShrink: 0 }}>
                                ${parseFloat(sku.price || sku.base_price || sku.base_cost || 0).toFixed(2)}
                              </div>
                            </div>
                          ))}
                        {skus.filter(sku => {
                          const matchesSearch = sku.name.toLowerCase().includes(skuSearch.toLowerCase()) ||
                            sku.code?.toLowerCase().includes(skuSearch.toLowerCase());
                          return matchesSearch;
                        }).length === 0 && (
                          <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted }}>
                            No products found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* Add Custom Item - for typing in custom line items */}
                {canAddManualItems() && (
                  <button
                    onClick={() => setShowCustomItemModal(true)}
                    style={{
                      background: colors.accent,
                      color: '#fff',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Plus size={16} />
                    Custom Item
                  </button>
                )}
              </div>
            </div>

            {quoteItems.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                background: colors.bgInput,
                borderRadius: '8px',
                border: `1px dashed ${colors.border}`,
                color: colors.textMuted
              }}>
                No items added yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {quoteItems.map(item => (
                  <div
                    key={item.id}
                    style={{
                      background: colors.bgInput,
                      padding: '1rem',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ fontWeight: 'bold', color: colors.text, flex: 1 }}>
                        {item.name}
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: colors.error,
                          cursor: 'pointer',
                          padding: '0.25rem'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                      gap: '0.75rem'
                    }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                          Quantity
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                          Unit Price {!canModifyPrices() && <span style={{ color: colors.warning, fontSize: '0.75rem' }}>(locked)</span>}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updatePrice(item.id, e.target.value)}
                          disabled={!canModifyPrices()}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: canModifyPrices() ? colors.bgCard : colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: colors.text,
                            fontSize: '0.9rem',
                            cursor: canModifyPrices() ? 'text' : 'not-allowed',
                            opacity: canModifyPrices() ? 1 : 0.7
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                          Total
                        </label>
                        <div style={{
                          padding: '0.5rem',
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.success,
                          fontSize: '0.9rem',
                          fontWeight: 'bold'
                        }}>
                          ${item.total_price.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals */}
          {quoteItems.length > 0 && (
            <div style={{
              background: colors.bgInput,
              padding: '1.5rem',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: colors.text }}>Subtotal:</span>
                <span style={{ color: colors.text, fontWeight: 'bold' }}>${calculateSubtotal().toFixed(2)}</span>
              </div>
              
              {salesTaxMode !== 'disabled' && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: colors.text }}>Tax:</span>
                    <input
                      type="number"
                      step="0.01"
                      min={salesTaxMode === 'required' ? 0.01 : 0}
                      max="1"
                      value={taxRate}
                      onChange={(e) => {
                        const newRate = parseFloat(e.target.value) || 0;
                        // If required mode, don't allow 0
                        if (salesTaxMode === 'required' && newRate === 0) return;
                        setTaxRate(newRate);
                      }}
                      style={{
                        width: '60px',
                        padding: '0.25rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        color: colors.text,
                        fontSize: '0.85rem',
                        textAlign: 'center'
                      }}
                    />
                    <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>({(taxRate * 100).toFixed(1)}%)</span>
                    {salesTaxMode === 'required' && (
                      <span style={{ color: colors.warning, fontSize: '0.75rem' }}>(required)</span>
                    )}
                  </div>
                  <span style={{ color: colors.text, fontWeight: 'bold' }}>${calculateTax().toFixed(2)}</span>
                </div>
              )}

              <div style={{
                borderTop: `2px solid ${colors.border}`,
                marginTop: '0.75rem',
                paddingTop: '0.75rem',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ color: colors.text, fontSize: '1.25rem', fontWeight: 'bold' }}>Total:</span>
                <span style={{ color: colors.success, fontSize: '1.5rem', fontWeight: 'bold' }}>
                  ${calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Deposit Section */}
          <div style={{
            marginBottom: '1.5rem',
            background: colors.bgInput,
            borderRadius: '8px',
            padding: '1rem',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: quoteDetails.deposit_required ? '1rem' : 0
            }}>
              <label style={{
                color: colors.text,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <DollarSign size={18} />
                Require Deposit
              </label>
              <button
                type="button"
                onClick={() => {
                  const newRequired = !quoteDetails.deposit_required;
                  setQuoteDetails({
                    ...quoteDetails,
                    deposit_required: newRequired,
                    deposit_amount: newRequired ? (calculateTotal() * (quoteDetails.deposit_percentage / 100)) : 0
                  });
                }}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  background: quoteDetails.deposit_required ? colors.success : colors.border,
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.2s'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: quoteDetails.deposit_required ? '26px' : '2px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'white',
                  transition: 'left 0.2s'
                }} />
              </button>
            </div>

            {quoteDetails.deposit_required && (
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {/* Percentage Quick Buttons */}
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Percentage of Total
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[25, 50, 75].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setQuoteDetails({
                          ...quoteDetails,
                          deposit_percentage: pct,
                          deposit_amount: calculateTotal() * (pct / 100)
                        })}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: quoteDetails.deposit_percentage === pct ? colors.accent : colors.bgCard,
                          border: quoteDetails.deposit_percentage === pct ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: quoteDetails.deposit_percentage === pct ? '#fff' : colors.text,
                          fontSize: '0.85rem',
                          fontWeight: quoteDetails.deposit_percentage === pct ? 'bold' : 'normal',
                          cursor: 'pointer'
                        }}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Amount */}
                <div style={{ flex: '1 1 150px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                    Deposit Amount
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute',
                      left: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: colors.success,
                      fontWeight: 'bold'
                    }}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={calculateTotal()}
                      value={quoteDetails.deposit_amount?.toFixed(2) || '0.00'}
                      onChange={(e) => {
                        const amount = parseFloat(e.target.value) || 0;
                        setQuoteDetails({
                          ...quoteDetails,
                          deposit_amount: amount,
                          deposit_percentage: calculateTotal() > 0 ? Math.round((amount / calculateTotal()) * 100) : 0
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem 0.5rem 1.75rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.success,
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {quoteDetails.deposit_required && quoteDetails.deposit_amount > 0 && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#1a3d2a',
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: colors.success, fontWeight: '500' }}>
                  Deposit Required: ${quoteDetails.deposit_amount.toFixed(2)}
                </span>
                <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Balance: ${(calculateTotal() - quoteDetails.deposit_amount).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Recurring Service Option - always show, customer can agree to recurring service terms */}
          <div style={{
              marginBottom: '1.5rem',
              background: colors.bgInput,
              borderRadius: '8px',
              padding: '1rem',
              border: `1px solid ${quoteDetails.is_recurring ? colors.info : colors.border}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: quoteDetails.is_recurring ? '1rem' : 0
              }}>
                <label style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Calendar size={18} />
                  Recurring Service
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setQuoteDetails({
                      ...quoteDetails,
                      is_recurring: !quoteDetails.is_recurring
                    });
                  }}
                  style={{
                    width: '52px',
                    height: '28px',
                    borderRadius: '14px',
                    background: quoteDetails.is_recurring ? colors.info : colors.border,
                    border: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 0.2s'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: quoteDetails.is_recurring ? '26px' : '2px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'white',
                    transition: 'left 0.2s'
                  }} />
                </button>
              </div>

              {quoteDetails.is_recurring && (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {/* Frequency Selection */}
                  <div style={{ flex: '1 1 200px' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                      Frequency
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {[
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'biweekly', label: 'Every 2 Weeks' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' }
                      ].map(freq => (
                        <button
                          key={freq.value}
                          type="button"
                          onClick={() => setQuoteDetails({
                            ...quoteDetails,
                            recurrence_rule: freq.value
                          })}
                          style={{
                            flex: '1 1 80px',
                            padding: '0.5rem',
                            background: quoteDetails.recurrence_rule === freq.value ? colors.info : colors.bgCard,
                            border: quoteDetails.recurrence_rule === freq.value ? `2px solid ${colors.info}` : `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            color: quoteDetails.recurrence_rule === freq.value ? '#fff' : colors.text,
                            fontSize: '0.8rem',
                            fontWeight: quoteDetails.recurrence_rule === freq.value ? 'bold' : 'normal',
                            cursor: 'pointer'
                          }}
                        >
                          {freq.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* End Date (Optional) */}
                  <div style={{ flex: '1 1 150px' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={quoteDetails.recurrence_end_date || ''}
                      onChange={(e) => setQuoteDetails({
                        ...quoteDetails,
                        recurrence_end_date: e.target.value || null
                      })}
                      min={new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text,
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              )}

              {quoteDetails.is_recurring && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: `${colors.info}20`,
                  borderRadius: '6px',
                  color: colors.info,
                  fontSize: '0.85rem'
                }}>
                  This quote is for <strong>{quoteDetails.recurrence_rule}</strong> recurring service
                  {quoteDetails.recurrence_end_date ? ` until ${new Date(quoteDetails.recurrence_end_date).toLocaleDateString()}` : ' (ongoing)'}.
                  Customer will see and agree to these terms when signing.
                </div>
              )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
              Notes (Optional)
            </label>
            <textarea
              value={quoteDetails.notes}
              onChange={(e) => setQuoteDetails({ ...quoteDetails, notes: e.target.value })}
              placeholder="Add any additional notes or terms..."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.95rem',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Include Terms Checkbox - only show if terms mode is optional */}
          {(quoteTermsMode === 'optional_on' || quoteTermsMode === 'optional_off') && (
            <div style={{
              marginBottom: '1.5rem',
              background: colors.bgInput,
              borderRadius: '8px',
              padding: '1rem',
              border: `1px solid ${colors.border}`
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                color: colors.text
              }}>
                <input
                  type="checkbox"
                  checked={quoteDetails.include_terms}
                  onChange={(e) => setQuoteDetails({ ...quoteDetails, include_terms: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    accentColor: colors.accent,
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ fontWeight: 'bold' }}>Include Terms & Conditions</div>
                  <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                    Add your company's terms and conditions to the signed quote PDF
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <button
              onClick={handleSaveQuote}
              style={{
                flex: 1,
                background: colors.success,
                color: '#000',
                border: 'none',
                padding: '1rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '48px'
              }}
            >
              <Save size={20} />
              {editingQuote ? 'Update Quote' : 'Save Quote'}
            </button>

            {/* Save & Sign button - for when customer is present */}
            <button
              onClick={saveAndSign}
              style={{
                flex: 1,
                background: colors.accent,
                color: '#fff',
                border: 'none',
                padding: '1rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '48px'
              }}
            >
              <FileText size={20} />
              Save & Sign
            </button>

            {/* Send Quote button - for remote signing */}
            <button
              onClick={saveAndSend}
              style={{
                flex: 1,
                background: colors.info || '#3498db',
                color: '#fff',
                border: 'none',
                padding: '1rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minHeight: '48px'
              }}
            >
              <Send size={20} />
              Send Quote
            </button>

            <button
              onClick={() => {
                resetForm();
                setShowQuotesList(true);
              }}
              style={{
                flex: isMobile ? 1 : 'none',
                background: colors.bgInput,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                padding: '1rem',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                minHeight: '48px'
              }}
            >
              Cancel
            </button>

            {/* Delete button - only show when editing existing quote */}
            {editingQuote && (
              <button
                onClick={() => {
                  deleteQuote(editingQuote.id);
                  resetForm();
                  setShowQuotesList(true);
                }}
                style={{
                  flex: isMobile ? 1 : 'none',
                  background: colors.error,
                  color: '#fff',
                  border: 'none',
                  padding: '1rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Trash2 size={20} />
                Delete Quote
              </button>
            )}
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignModal && selectedQuoteForSigning && signingCustomer && (
        <QuoteSignatureModal
          quote={selectedQuoteForSigning}
          customer={signingCustomer}
          quoteItems={signingQuoteItems}
          companyId={companyId}
          quoteTermsMode={quoteTermsMode}
          onClose={() => {
            setShowSignModal(false);
            setSelectedQuoteForSigning(null);
            setSigningCustomer(null);
            setSigningQuoteItems([]);
          }}
          onBack={() => {
            setShowSignModal(false);
            setSelectedQuoteForSigning(null);
            setSigningCustomer(null);
            setSigningQuoteItems([]);
          }}
          onSigned={(signedPdfUrl) => {
            // After signing, show post-signature workflow
            setSignedQuote(selectedQuoteForSigning);
            setShowSignModal(false);
            setShowPostSignatureWorkflow(true);
            // Clear signing state but keep quote for workflow
            setSelectedQuoteForSigning(null);
            setSigningCustomer(null);
            setSigningQuoteItems([]);
            fetchQuotes();
          }}
        />
      )}

      {/* Post-Signature Workflow */}
      {showPostSignatureWorkflow && signedQuote && (
        <PostSignatureWorkflow
          quote={signedQuote}
          companyId={companyId}
          onClose={() => {
            setShowPostSignatureWorkflow(false);
            setSignedQuote(null);
            resetForm();
          }}
          onComplete={(job) => {
            setShowPostSignatureWorkflow(false);
            setSignedQuote(null);
            resetForm();
            showSuccess(`Job ${job.job_number} created successfully!`);
          }}
        />
      )}

      {/* Schedule Job Modal */}
      {showScheduleModal && selectedQuoteForScheduling && (
        <ScheduleJobModal
          quote={selectedQuoteForScheduling}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedQuoteForScheduling(null);
          }}
          onScheduled={scheduleJobFromQuote}
          recurringEnabled={recurringEnabled}
          companyId={companyId}
        />
      )}

      {/* Manual Price Entry Modal */}
      {showManualPriceModal && pendingSku && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%',
            border: `2px solid ${colors.warning}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: `${colors.warning}30`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PenLine size={20} style={{ color: colors.warning }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                  Enter Price
                </h3>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Manual pricing required
                </div>
              </div>
            </div>

            <div style={{
              background: colors.bgInput,
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontWeight: '600', color: colors.text, marginBottom: '0.25rem' }}>
                {pendingSku.name}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', fontFamily: 'monospace' }}>
                {pendingSku.code}
              </div>
              {pendingSku.base_cost > 0 && (
                <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginTop: '0.5rem' }}>
                  Reference: ${(pendingSku.base_cost || pendingSku.cost || 0).toFixed(2)} (base cost)
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Unit Price *
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ color: colors.warning, fontSize: '1.25rem', fontWeight: '600' }}>$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={manualPrice}
                  onChange={(e) => setManualPrice(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `2px solid ${colors.warning}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1.25rem',
                    fontFamily: 'monospace',
                    fontWeight: '600'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmManualPrice();
                    if (e.key === 'Escape') {
                      setShowManualPriceModal(false);
                      setPendingSku(null);
                    }
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  setShowManualPriceModal(false);
                  setPendingSku(null);
                }}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.border,
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmManualPrice}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: `linear-gradient(135deg, ${colors.warning} 0%, #e67e22 100%)`,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Check size={18} />
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Modal (Manual Entry) */}
      {showCustomItemModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            maxWidth: '500px',
            width: '100%',
            border: `2px solid ${colors.success}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: `${colors.success}30`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Plus size={20} style={{ color: colors.success }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                  Add Line Item
                </h3>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Enter item details manually
                </div>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Description *
              </label>
              <input
                type="text"
                value={customItemDescription}
                onChange={(e) => setCustomItemDescription(e.target.value)}
                placeholder="e.g., Spring replacement - 2 car garage"
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Price and Quantity Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Unit Price *
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: colors.success,
                    fontWeight: 'bold'
                  }}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={customItemPrice}
                    onChange={(e) => setCustomItemPrice(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.875rem 0.875rem 0.875rem 1.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={customItemQuantity}
                  onChange={(e) => setCustomItemQuantity(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            {/* Preview */}
            {customItemPrice && parseFloat(customItemPrice) > 0 && (
              <div style={{
                background: '#2a3d2a',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: colors.success }}>Line Total:</span>
                <span style={{ color: colors.success, fontSize: '1.25rem', fontWeight: 'bold' }}>
                  ${((parseFloat(customItemPrice) || 0) * (parseInt(customItemQuantity) || 1)).toFixed(2)}
                </span>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  setShowCustomItemModal(false);
                  setCustomItemDescription('');
                  setCustomItemPrice('');
                  setCustomItemQuantity('1');
                }}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.border,
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '48px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={addCustomItem}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: `linear-gradient(135deg, ${colors.success} 0%, #45b393 100%)`,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  minHeight: '48px'
                }}
              >
                <Check size={18} />
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Quote Modal */}
      {showSendModal && editingQuote && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: isMobile ? '1.5rem' : '2rem',
            maxWidth: '500px',
            width: '100%',
            border: `2px solid ${colors.info || '#3498db'}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: `${colors.info || '#3498db'}30`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Send size={20} style={{ color: colors.info || '#3498db' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                  Send Quote for Signing
                </h3>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  {editingQuote.quote_number}
                </div>
              </div>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  marginLeft: 'auto',
                  background: 'transparent',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Customer Info - Editable */}
            {(() => {
              const customer = customers.find(c => c.id === editingQuote.customer_id);
              return customer ? (
                <div style={{
                  background: colors.bgInput,
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold' }}>
                      {customer.name}
                    </div>
                    <button
                      onClick={() => setEditingContactInfo(!editingContactInfo)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: colors.info || '#3498db',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Edit3 size={14} />
                      {editingContactInfo ? 'Done' : 'Edit'}
                    </button>
                  </div>

                  {editingContactInfo ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                          Email
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Mail size={14} style={{ color: colors.textMuted }} />
                          <input
                            type="email"
                            value={sendModalEmail}
                            onChange={(e) => setSendModalEmail(e.target.value)}
                            placeholder="customer@email.com"
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: colors.bgCard,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.9rem'
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                          Phone
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <MessageSquare size={14} style={{ color: colors.textMuted }} />
                          <input
                            type="tel"
                            value={sendModalPhone}
                            onChange={(e) => setSendModalPhone(e.target.value)}
                            placeholder="555-123-4567"
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: colors.bgCard,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              color: colors.text,
                              fontSize: '0.9rem'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ color: colors.textMuted, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Mail size={14} /> {sendModalEmail || <span style={{ fontStyle: 'italic' }}>No email</span>}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                        <MessageSquare size={14} /> {sendModalPhone || <span style={{ fontStyle: 'italic' }}>No phone</span>}
                      </div>
                    </>
                  )}
                </div>
              ) : null;
            })()}

            {/* Additional Recipients */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Additional Recipients
                </label>
                <button
                  onClick={() => setAdditionalRecipients([...additionalRecipients, { email: '', phone: '' }])}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <UserPlus size={14} />
                  Add
                </button>
              </div>

              {additionalRecipients.length === 0 ? (
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', fontStyle: 'italic' }}>
                  No additional recipients
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {additionalRecipients.map((recipient, idx) => (
                    <div key={idx} style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Recipient {idx + 2}</span>
                        <button
                          onClick={() => setAdditionalRecipients(additionalRecipients.filter((_, i) => i !== idx))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: colors.danger || '#e74c3c',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Mail size={12} style={{ color: colors.textMuted }} />
                            <input
                              type="email"
                              value={recipient.email}
                              onChange={(e) => {
                                const updated = [...additionalRecipients];
                                updated[idx].email = e.target.value;
                                setAdditionalRecipients(updated);
                              }}
                              placeholder="Email"
                              style={{
                                flex: 1,
                                padding: '0.4rem',
                                background: colors.bgCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '4px',
                                color: colors.text,
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ flex: '1 1 150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <MessageSquare size={12} style={{ color: colors.textMuted }} />
                            <input
                              type="tel"
                              value={recipient.phone}
                              onChange={(e) => {
                                const updated = [...additionalRecipients];
                                updated[idx].phone = e.target.value;
                                setAdditionalRecipients(updated);
                              }}
                              placeholder="Phone"
                              style={{
                                flex: 1,
                                padding: '0.4rem',
                                background: colors.bgCard,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '4px',
                                color: colors.text,
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quote Link */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Quote Link
              </label>
              <div style={{
                display: 'flex',
                gap: '0.5rem'
              }}>
                <input
                  type="text"
                  readOnly
                  value={getQuoteUrl(editingQuote.id)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    fontSize: '0.85rem'
                  }}
                />
                <button
                  onClick={copyQuoteLink}
                  style={{
                    padding: '0.75rem 1rem',
                    background: quoteLinkCopied ? colors.success : colors.bgInput,
                    border: `1px solid ${quoteLinkCopied ? colors.success : colors.border}`,
                    borderRadius: '8px',
                    color: quoteLinkCopied ? '#fff' : colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {quoteLinkCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {quoteLinkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Send Method */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.75rem',
                color: colors.text,
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}>
                Send via
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[
                  { value: 'email', label: 'Email', icon: Mail },
                  { value: 'sms', label: 'SMS', icon: MessageSquare },
                  { value: 'both', label: 'Both', icon: Send }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSendMethod(option.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: sendMethod === option.value ? `${colors.info || '#3498db'}20` : colors.bgInput,
                      border: `2px solid ${sendMethod === option.value ? colors.info || '#3498db' : colors.border}`,
                      borderRadius: '8px',
                      color: sendMethod === option.value ? colors.info || '#3498db' : colors.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontWeight: sendMethod === option.value ? 'bold' : 'normal',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <option.icon size={16} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.border,
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minHeight: '48px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendQuote}
                disabled={sendingQuote}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: sendingQuote ? colors.border : `linear-gradient(135deg, ${colors.info || '#3498db'} 0%, #2980b9 100%)`,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: sendingQuote ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  minHeight: '48px',
                  opacity: sendingQuote ? 0.7 : 1
                }}
              >
                {sendingQuote ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Quote
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
